/**
 * Viability app data hooks — thin projections over the live planning engine.
 *
 * Same engine, same rows, no new tables: scenario + run via
 * planningEngineService (runFeasibilityCheck persists to planning_runs),
 * live-items fallback via itemToPlanningAdapter when nothing is configured,
 * launch readiness from the same service getters the B-Mode checklist uses.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  activateScenario,
  getCapacityRows,
  getPriorities,
  getScenarios,
  getTeamParticipation,
  readPersistedEngineInput,
  runEngineWithInput,
  runFeasibilityCheck,
} from '@/lib/planningEngineService';
import { getLaunchProfiles, getSupplyCalendar } from '@/lib/timePhasedPlanningService';
import { getFundingEnvelopes } from '@/lib/timePhasedFinancialService';
import { itemsToPlanningEngineInput, LIVE_ITEMS_SCENARIO } from '@/lib/itemToPlanningAdapter';
import { buildPriorityOutcomes, type PriorityOutcome } from '@/lib/planningFeasibilityLanguage';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '@/stores/prioritization/itemRelationshipsStore';
import { getItemEffortBandHours } from '@/stores/prioritization/planningDefaultsStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import type { PlanningEngineOutput, Scenario } from '@/lib/planningEngineModule.types';

export interface ViabilityRun {
  projectId: string | null;
  scenario: Scenario | null;
  /** Every scenario in the workspace, so the screen can say which one it uses. */
  scenarios: Scenario[];
  scenariosLoaded: boolean;
  usingLiveItemsFallback: boolean;
  output: PlanningEngineOutput | null;
  outcomes: PriorityOutcome[];
  running: boolean;
  error: string | null;
  capacityRowCount: number | null;
  rerun: () => void;
  /** Run the check on another scenario. Makes it the active scenario — the
   *  same act as picking it in the planning engine, so both agree. */
  chooseScenario: (scenarioId: string) => Promise<void>;
  /** Set when switching scenarios failed. */
  chooseError: string | null;
}

/** Load scenarios, auto-run the engine once, expose plain-language outcomes. */
export function useViabilityRun(): ViabilityRun {
  const projectId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchEdges = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenariosLoaded, setScenariosLoaded] = useState(false);
  const [output, setOutput] = useState<PlanningEngineOutput | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capacityRowCount, setCapacityRowCount] = useState<number | null>(null);
  const [chooseError, setChooseError] = useState<string | null>(null);
  /** The scenario id the last automatic run used; a new id runs again. */
  const autoRanFor = useRef<string | null | undefined>(undefined);

  const scenario = useMemo(
    () => scenarios.find((s) => s.active) ?? scenarios[0] ?? null,
    [scenarios]
  );

  const run = useCallback(
    async (forceRerun: boolean) => {
      if (!projectId) return;
      setRunning(true);
      setError(null);
      try {
        await Promise.all([fetchItems(), fetchEdges()]);
        const items = useItemsStore.getState().items;
        const edges = useItemRelationshipsStore.getState().edges;
        if (scenario) {
          const engineInput = readPersistedEngineInput();
          setOutput(
            await runFeasibilityCheck({ projectId, scenario, engineInput, items, edges, forceRerun })
          );
        } else {
          // Guide-moment default: no scenario yet → run in memory on live items.
          setOutput(
            runEngineWithInput(
              itemsToPlanningEngineInput(
                items,
                edges,
                { ...LIVE_ITEMS_SCENARIO, projectId },
                getItemEffortBandHours(),
              )
            )
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The viability check failed.');
      } finally {
        setRunning(false);
      }
    },
    [projectId, scenario, fetchItems, fetchEdges]
  );

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void getScenarios(projectId)
      .then((list) => {
        if (!cancelled) {
          setScenarios(list);
          setScenariosLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setScenariosLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !scenario?.id) {
      setCapacityRowCount(null);
      return;
    }
    let cancelled = false;
    void getCapacityRows(projectId, scenario.id)
      .then((rows) => {
        if (!cancelled) setCapacityRowCount(rows.length);
      })
      .catch(() => {
        if (!cancelled) setCapacityRowCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, scenario?.id]);

  useEffect(() => {
    if (!scenariosLoaded || !projectId) return;
    const id = scenario?.id ?? null;
    if (autoRanFor.current === id) return;
    autoRanFor.current = id;
    void run(false);
  }, [scenariosLoaded, projectId, scenario?.id, run]);

  const chooseScenario = useCallback(
    async (scenarioId: string) => {
      if (!projectId || scenarioId === scenario?.id) return;
      const previous = scenarios;
      setChooseError(null);
      setOutput(null);
      setScenarios((list) => list.map((s) => ({ ...s, active: s.id === scenarioId })));
      try {
        await activateScenario(scenarioId, projectId);
      } catch (err) {
        setScenarios(previous);
        setChooseError(err instanceof Error ? err.message : 'Could not switch to that scenario.');
      }
    },
    [projectId, scenario?.id, scenarios]
  );

  const outcomes = useMemo(() => (output ? buildPriorityOutcomes(output) : []), [output]);

  return {
    projectId,
    scenario,
    scenarios,
    scenariosLoaded,
    usingLiveItemsFallback: scenariosLoaded && !scenario,
    output,
    outcomes,
    running,
    error,
    capacityRowCount,
    rerun: () => void run(true),
    chooseScenario,
    chooseError,
  };
}

export interface LaunchReadiness {
  loaded: boolean;
  participationCount: number;
  supplyRows: number;
  profileCount: number;
  prioritiesTotal: number;
  prioritiesWithProfile: number;
  fundingEnvelopeCount: number;
}

/** The three launch-planning inputs, counted the same way the B-Mode
 *  "Launch planning setup" checklist counts them. */
export function useLaunchReadiness(
  projectId: string | null,
  scenarioId: string | null
): LaunchReadiness {
  const [state, setState] = useState<LaunchReadiness>({
    loaded: false,
    participationCount: 0,
    supplyRows: 0,
    profileCount: 0,
    prioritiesTotal: 0,
    prioritiesWithProfile: 0,
    fundingEnvelopeCount: 0,
  });

  useEffect(() => {
    if (!projectId || !scenarioId) return;
    let cancelled = false;
    void (async () => {
      try {
        const [supply, profiles, priorities, participation, envelopes] = await Promise.all([
          getSupplyCalendar(scenarioId),
          getLaunchProfiles(projectId),
          getPriorities(projectId),
          getTeamParticipation(projectId),
          getFundingEnvelopes(scenarioId),
        ]);
        if (cancelled) return;
        const scoped = priorities.filter((p) => p.scenarioId === scenarioId);
        const inScope = scoped.length > 0 ? scoped : priorities;
        setState({
          loaded: true,
          participationCount: participation.length,
          supplyRows: supply.length,
          profileCount: profiles.length,
          prioritiesTotal: inScope.length,
          prioritiesWithProfile: inScope.filter((p) => p.launchProfileId).length,
          fundingEnvelopeCount: envelopes.length,
        });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loaded: true }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, scenarioId]);

  return state;
}
